import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Grid,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    CircularProgress,
    Alert,
    LinearProgress
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { MethodologyAssessmentResult, RequirementAssessment } from '../services/methodology';

interface MethodologyAssessmentResultsProps {
    assessment: MethodologyAssessmentResult;
}

const MethodologyAssessmentResults: React.FC<MethodologyAssessmentResultsProps> = ({ assessment }) => {
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const toggleCategory = (category: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(category)) {
            newExpanded.delete(category);
        } else {
            newExpanded.add(category);
        }
        setExpandedCategories(newExpanded);
    };

    // Group requirements by category
    const groupByCategory = (requirements: RequirementAssessment[]) => {
        const grouped: Record<string, RequirementAssessment[]> = {};
        requirements.forEach(req => {
            const category = req.category || 'other';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(req);
        });
        return grouped;
    };

    const achievedByCategory = groupByCategory(assessment.achieved_requirements || []);
    const missingByCategory = groupByCategory(assessment.missing_requirements || []);

    // Get score colour
    const getScoreColor = (score: number) => {
        if (score >= 80) return '#4caf50'; // Green
        if (score >= 50) return '#ff9800'; // Amber
        return '#f44336'; // Red
    };

    const getScoreLabel = (score: number) => {
        if (score >= 80) return 'Excellent';
        if (score >= 50) return 'Good';
        return 'Needs Improvement';
    };

    // Format category name for display
    const formatCategoryName = (category: string) => {
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const overallScore = assessment.overall_score || 0;
    const totalRequirements = assessment.total_requirements || 0;
    const requirementsMet = assessment.requirements_met || 0;

    return (
        <Box sx={{ mt: 3 }}>
            {/* Percentage Met Indicator */}
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 4, 
                    mb: 4,
                    background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
                    border: '1px solid rgba(0, 165, 153, 0.3)',
                    textAlign: 'center'
                }}
            >
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                    Overall Compliance Score
                </Typography>
                
                {/* Circular Progress */}
                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                    <CircularProgress
                        variant="determinate"
                        value={overallScore}
                        size={120}
                        thickness={4}
                        sx={{
                            color: getScoreColor(overallScore),
                            transform: 'rotate(-90deg)'
                        }}
                    />
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            variant="h4"
                            component="div"
                            sx={{
                                color: 'white',
                                fontWeight: 700
                            }}
                        >
                            {Math.round(overallScore)}%
                        </Typography>
                    </Box>
                </Box>
                
                <Typography 
                    variant="h5" 
                    sx={{ 
                        color: getScoreColor(overallScore),
                        fontWeight: 600,
                        mb: 1
                    }}
                >
                    {getScoreLabel(overallScore)}
                </Typography>
                
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {requirementsMet} of {totalRequirements} requirements met
                </Typography>
                
                {/* Progress Bar */}
                <Box sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
                    <LinearProgress
                        variant="determinate"
                        value={overallScore}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: getScoreColor(overallScore),
                                borderRadius: 5
                            }
                        }}
                    />
                </Box>
            </Paper>

            {/* Achieved Requirements Section */}
            {assessment.achieved_requirements && assessment.achieved_requirements.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Achieved Requirements
                        </Typography>
                        <Chip 
                            label={`${assessment.achieved_requirements.length} met`}
                            color="success"
                            size="small"
                        />
                    </Box>
                    
                    {Object.entries(achievedByCategory).map(([category, requirements]) => (
                        <Accordion
                            key={`achieved-${category}`}
                            expanded={expandedCategories.has(`achieved-${category}`)}
                            onChange={() => toggleCategory(`achieved-${category}`)}
                            sx={{ mb: 1 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Chip 
                                        label={formatCategoryName(category)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {requirements.length} requirement{requirements.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {requirements.map((req, idx) => (
                                        <Paper
                                            key={idx}
                                            elevation={1}
                                            sx={{
                                                p: 2,
                                                bgcolor: 'background.paper',
                                                borderLeft: '4px solid #4caf50'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
                                                <CheckCircleIcon sx={{ color: '#4caf50', mt: 0.5 }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                        {req.requirement_text}
                                                    </Typography>
                                                    {req.evidence && (
                                                        <Alert 
                                                            severity="success"
                                                            variant="outlined"
                                                            icon={false}
                                                            sx={{ 
                                                                mt: 1,
                                                                bgcolor: 'transparent',
                                                                borderColor: '#4caf50',
                                                                color: 'text.primary',
                                                                '& .MuiAlert-message': {
                                                                    fontSize: '0.875rem'
                                                                }
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                Evidence from methodology:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                                                                "{req.evidence}"
                                                            </Typography>
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Paper>
            )}

            {/* Missing Requirements Section */}
            {assessment.missing_requirements && assessment.missing_requirements.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <CancelIcon sx={{ color: '#f44336', fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Missing Requirements
                        </Typography>
                        <Chip 
                            label={`${assessment.missing_requirements.length} missing or partial`}
                            color="error"
                            size="small"
                        />
                    </Box>
                    
                    {Object.entries(missingByCategory).map(([category, requirements]) => (
                        <Accordion
                            key={`missing-${category}`}
                            expanded={expandedCategories.has(`missing-${category}`)}
                            onChange={() => toggleCategory(`missing-${category}`)}
                            sx={{ mb: 1 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Chip 
                                        label={formatCategoryName(category)}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                    />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {requirements.length} requirement{requirements.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {requirements.map((req, idx) => {
                                        const isPartial = req.status === 'partial';
                                        return (
                                            <Paper
                                                key={idx}
                                                elevation={1}
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'background.paper',
                                                    borderLeft: `4px solid ${isPartial ? '#ff9800' : '#f44336'}`
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
                                                    {isPartial ? (
                                                        <WarningIcon sx={{ color: '#ff9800', mt: 0.5 }} />
                                                    ) : (
                                                        <CancelIcon sx={{ color: '#f44336', mt: 0.5 }} />
                                                    )}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                {req.requirement_text}
                                                            </Typography>
                                                            {isPartial && (
                                                                <Chip 
                                                                    label="Partial" 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: '#ff9800',
                                                                        color: 'white',
                                                                        height: 20,
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            )}
                                                            {req.weight === 2 && (
                                                                <Chip 
                                                                    label="Required" 
                                                                    size="small" 
                                                                    color="error"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                        
                                                        {isPartial && req.evidence && (
                                                            <Alert 
                                                                severity="warning" 
                                                                variant="outlined"
                                                                icon={false}
                                                                sx={{ 
                                                                    mt: 1,
                                                                    bgcolor: 'transparent',
                                                                    borderColor: '#ff9800',
                                                                    color: 'text.primary',
                                                                    '& .MuiAlert-message': {
                                                                        fontSize: '0.875rem'
                                                                    }
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                    Partially addressed:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                                                                    "{req.evidence}"
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                        
                                                        {req.missing_details && (
                                                            <Alert 
                                                                severity="error" 
                                                                variant="outlined"
                                                                icon={false}
                                                                sx={{ 
                                                                    mt: 1,
                                                                    bgcolor: 'transparent',
                                                                    borderColor: '#f44336',
                                                                    color: 'text.primary',
                                                                    '& .MuiAlert-message': {
                                                                        fontSize: '0.875rem'
                                                                    }
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                    What's missing:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                                    {req.missing_details}
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                        
                                                        {req.recommendations && (
                                                            <Alert 
                                                                severity="info" 
                                                                variant="outlined"
                                                                icon={false}
                                                                sx={{ 
                                                                    mt: 1,
                                                                    bgcolor: 'transparent',
                                                                    borderColor: '#2196f3',
                                                                    color: 'text.primary',
                                                                    '& .MuiAlert-message': {
                                                                        fontSize: '0.875rem'
                                                                    }
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                    Recommendations:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                                    {req.recommendations}
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Paper>
            )}

            {/* Empty State */}
            {(!assessment.achieved_requirements || assessment.achieved_requirements.length === 0) &&
             (!assessment.missing_requirements || assessment.missing_requirements.length === 0) && (
                <Alert severity="info">
                    No requirements assessed. Please ensure the methodology text and ToR extraction are valid.
                </Alert>
            )}
        </Box>
    );
};

export default MethodologyAssessmentResults;

